import type { components, CountdownApiClient } from '@countdown/api-client';
import {
  type FullDoorLayout,
  fitLayout,
  fullLayout,
  layoutRequest,
  sameLayout,
} from '@countdown/door-editor';
import { DEFAULT_DOOR_LAYOUT, type DoorWindowLayout, Required } from '@countdown/theme';
import { type ReactElement, type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { type PreviewLogo } from '@/components/CalendarDesign';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DoorDesignEditor } from '@/components/DoorDesignEditor';
import { DoorEditor } from '@/components/DoorEditor';
import { HelpTip } from '@/components/HelpTip';
import { ParticipantPreview } from '@/components/ParticipantPreview';
import { EditorLayout } from '@/components/PreviewLayout';
import {
  type SaveResult,
  SaveBarSlot,
  useSaveBarDirty,
  useSaveFirst,
  useSaveSection,
} from '@/components/SaveBar';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/table';
import {
  type ActionDetail,
  type ActionOutcome,
  LOAD_FAILED,
  ROLE_MISSING,
  approveUnscanned,
  assetAddress,
  loadActionDetail,
  loadEditorialBookings,
  loadSponsorDetail,
  mediaSrc,
  placeBooking,
  pullEmergencySwitch,
  releaseBooking,
  sendAccessInvitation,
  runEditorialAction,
  saveBookingDocument,
  saveFieldDoorLayout,
  sendInvitation,
  showInvitationLink,
  originalAddress,
  uploadBookingMedia,
  useResource,
} from '@/lib/api';
import { systemClock } from '@/lib/clock';
import { HELP_TIPS } from '@/lib/help';
import {
  type ChecklistItem,
  contentChecklist,
  hasOpened,
  isPublic,
  missingLabel,
} from '@/lib/door';
import {
  type BookingState,
  NUMBER_FORMAT,
  SCAN_STATUS_LABELS,
  can,
  formatDate,
  formatDateTime,
  formatDeadlineAfter,
  formatOpening,
} from '@/lib/labels';
import { CUSTOMER_WORDING, SPONSOR_WORDING, type Wording, firmPath, wordingOf } from '@/lib/wording';
import { DoorState, useActionContext } from '@/pages/ActionPage';
import { AccessInvitation } from '@/pages/SponsorsPage';

type BookingList = components['schemas']['EditorialBookingListView'];
type Booking = components['schemas']['EditorialBookingView'];
type FieldView = components['schemas']['BackofficeFieldView'];
type SponsorView = components['schemas']['BackofficeSponsorView'];

/**
 * Runs one write and reloads what it moved; `overview` also when sponsor or door counts change.
 * `onSuccess` runs only when the write went through, so a refused one keeps what was typed.
 */
type Run = (
  write: () => Promise<ActionOutcome>,
  options?: { readonly overview?: boolean; readonly onSuccess?: () => void },
) => void;

/** `RELEASE_BLOCKING_STATES` in `services/campaign.py`: seen by the public, or stopped. */
const RELEASE_BLOCKED: ReadonlySet<BookingState> = new Set(['live', 'finished', 'depublished']);

/** Not handed in, not released: a save of the publisher's writes the door itself. */
const OPEN_STATES: ReadonlySet<BookingState> = new Set(['invited', 'draft', 'changes_requested']);

/** Handed in by the sponsor, waiting for the editors. */
const REVIEW_STATES: ReadonlySet<BookingState> = new Set(['submitted', 'in_review']);

/**
 * `_SENDABLE_ROW_STATES` in `routers/backoffice.py`: without a correction only a door the
 * sponsor never handed in goes to them for approval; what they handed in is released as it is.
 */
const SENDABLE_ROW_STATES: ReadonlySet<BookingState> = new Set(['draft', 'changes_requested']);

const INPUT_CLASS = 'mt-1 w-full border border-input bg-background px-3 py-2 text-sm';
const LABEL_CLASS = 'mt-3 block text-sm font-medium';

function deadline(value: string | null): string {
  return value === null ? 'offen' : formatDateTime(value);
}

/**
 * The verdicts "Ungeprüft freigeben" accepts, for the image and the logo alike: the scan has
 * not run or could not answer. `ACCEPTABLE_UNSCANNED` on the server, which leaves a rejected
 * logo unpublished.
 */
const ACCEPTABLE_UNSCANNED: ReadonlySet<string | null> = new Set(['pending', 'failed']);

/**
 * The image verdicts "Ungeprüft freigeben" lets through (`null`: no image), named like
 * `_RELEASABLE_IMAGE` on the server: a verdict missing here keeps the button away.
 */
const RELEASABLE_IMAGE: ReadonlySet<string | null> = new Set([
  null,
  'clean',
  ...ACCEPTABLE_UNSCANNED,
]);

/** Whether "Ungeprüft freigeben" has something to accept and nothing it must refuse. */
export function acceptsUnscanned(image: string | null, logo: string | null): boolean {
  return (
    RELEASABLE_IMAGE.has(image) &&
    (ACCEPTABLE_UNSCANNED.has(image) || ACCEPTABLE_UNSCANNED.has(logo))
  );
}

/**
 * Why the release is locked, when a file of the door is not scanned clean; `null` otherwise.
 * A file, since a door carries pictures, videos and caption files (package H); the lines of
 * the door below say which one.
 */
export function scanNotice(status: string | null): string | null {
  if (status === null || status === 'clean') {
    return null;
  }
  if (status === 'rejected') {
    // The wording of the 409 from `bookings/approve-unscanned`.
    return 'Die Prüfung hat eine Datei des Beitrags abgelehnt. Er lässt sich nicht freigeben.';
  }
  const label = SCAN_STATUS_LABELS[status];
  return label === undefined
    ? 'Eine Datei ist nicht geprüft. Freigabe gesperrt.'
    : `Eine Datei ist nicht geprüft (${label}). Freigabe gesperrt.`;
}

/** Why the waiting logo will not go out with "Freigeben"; `null` when it will or none waits. */
export function logoNotice(status: string | null): string | null {
  if (status === null || status === 'clean') {
    return null;
  }
  if (status === 'rejected') {
    return 'Die Prüfung hat das Logo abgelehnt. Es wird nicht veröffentlicht.';
  }
  const label = SCAN_STATUS_LABELS[status];
  const verdict = label === undefined ? '' : ` (${label})`;
  return `Logo nicht geprüft${verdict}. »Freigeben« veröffentlicht es nicht.`;
}

/**
 * The placement is what sends the invitation once the action is live: the hourly booking run
 * picks up every booking that was never invited. The one case worth a sentence is the one
 * where no mail will ever leave, unless none is meant to: the publisher's own sponsor (H3).
 */
function placedMessage(position: number, sponsor: SponsorView): string {
  return sponsor.contact_configured || sponsor.is_own
    ? `Türchen ${position} belegt.`
    : `Türchen ${position} belegt. Keine Kontakt-E-Mail hinterlegt.`;
}

function unavailable(status: string): string {
  if (status === 'loading') {
    return 'Wird geladen …';
  }
  if (status === 'forbidden') {
    return ROLE_MISSING;
  }
  return status === 'unauthenticated'
    ? 'Die Anmeldung ist abgelaufen. Bitte die Seite neu laden.'
    : LOAD_FAILED;
}

/**
 * One block of the door page under its h2: an h3 and space, no frame (D43). `danger` marks the
 * steps at the foot of the page that take a door or its booking away.
 */
function Section({
  title,
  danger = false,
  children,
}: {
  readonly title: string;
  readonly danger?: boolean;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section
      className={
        danger ? 'mt-10 max-w-2xl border-t border-destructive/30 pt-6' : 'mt-8 max-w-2xl'
      }
    >
      <h3 className={danger ? 'font-sans text-base font-bold text-destructive' : 'font-sans text-base font-bold'}>
        {title}
      </h3>
      {children}
    </section>
  );
}

/** The answer to the last write; the invitation link is the one answer that carries a link. */
function Notice({ outcome }: { readonly outcome: ActionOutcome }): ReactElement {
  const link = outcome.ok ? outcome.inviteUrl : undefined;
  return (
    <div className="mt-4 rounded-md bg-surface-sunken px-4 py-3 text-sm" role="status" aria-live="polite">
      <p>{outcome.message ?? 'Erledigt.'}</p>
      {link !== undefined ? (
        <a className="break-all underline" href={link} target="_blank" rel="noreferrer">
          {link}
        </a>
      ) : null}
    </div>
  );
}

/**
 * This door's own number and logo place (D29, D31), for the roles that configure the action.
 * The preview shows the logo of the booked sponsor, so number and logo can be fitted to each
 * other; an unbooked door shows the action's placeholder picture.
 */
function DoorNumber({
  field,
  actionId,
  readOnly,
  onSaved,
}: {
  readonly field: FieldView;
  readonly actionId: string;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}): ReactElement | null {
  const load = useCallback(
    (api: CountdownApiClient) => loadActionDetail(api, actionId),
    [actionId],
  );
  const detail = useResource<ActionDetail>(load);
  const [sponsorLogo, setSponsorLogo] = useState<PreviewLogo | null>(null);
  const sponsorId = field.sponsor_id ?? null;

  useEffect(() => {
    if (sponsorId === null) {
      setSponsorLogo(null);
      return undefined;
    }
    let current = true;
    loadSponsorDetail(sponsorId)
      .then((sponsor) => {
        const logo = sponsor?.logo ?? null;
        if (current) {
          setSponsorLogo(logo === null ? null : { src: mediaSrc(logo.path), alt: logo.alt_text });
        }
      })
      .catch((error: unknown) => {
        console.error('[DoorPage] sponsor logo for the preview failed', error);
      });
    return () => {
      current = false;
    };
  }, [sponsorId]);

  if (detail.status !== 'ready') {
    return null;
  }
  const placeholder = detail.data.placeholder_image ?? null;
  const logo =
    sponsorLogo ??
    (placeholder === null ? null : { src: mediaSrc(placeholder.path), alt: placeholder.alt_text });

  const own = field.door_number_style != null || field.logo_placement != null;
  // Rarely needed: folded away under the statistics, open where this door has its own look.
  return (
    <details className="mt-8 max-w-2xl" open={own}>
      <summary className="min-h-11 cursor-pointer py-2">
        <h3 className="inline font-sans text-base font-bold">Darstellung dieses Türchens</h3>
      </summary>
      <DoorDesignEditor
        key={JSON.stringify([field.door_number_style ?? null, field.logo_placement ?? null])}
        actionId={actionId}
        position={field.position}
        own={field.door_number_style ?? null}
        ownLogo={field.logo_placement ?? null}
        actionStyle={detail.data.door_number_style ?? null}
        calendarStyle={detail.data.calendar_style}
        logo={logo}
        readOnly={readOnly}
        onSaved={onSaved}
      />
    </details>
  );
}

/**
 * An unbooked door: which sponsor. The prizes are the door's (its prize tab, D94).
 *
 * A select rather than a typed number: on the door page the door is already chosen, and what
 * is left to pick is a company, which a list spells correctly. Inactive sponsors are left out,
 * their portal access is switched off.
 */
export function PlacementForm({
  position,
  actionId,
  sponsors,
  locked,
  run,
  wording = SPONSOR_WORDING,
}: {
  readonly position: number;
  readonly actionId: string;
  readonly sponsors: readonly SponsorView[];
  readonly locked: boolean;
  readonly run: Run;
  /** »Sponsor« or »Kunde« (E8). */
  readonly wording?: Wording;
}): ReactElement {
  const [sponsorId, setSponsorId] = useState('');
  const choices = sponsors.filter((sponsor) => sponsor.status !== 'inactive');
  const chosen = choices.find((sponsor) => sponsor.sponsor_id === sponsorId);
  const valid = chosen !== undefined;

  if (choices.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        Noch kein {wording.nominative}.{' '}
        <Link className="underline underline-offset-4" to={`/sponsoren${wording.view}`}>
          {wording.plural}
        </Link>
      </p>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) {
          return;
        }
        run(
          async () => {
            const result = await placeBooking(
              { field_position: position, sponsor_id: chosen.sponsor_id },
              actionId,
            );
            return result.ok
              ? { ok: true, message: placedMessage(position, chosen) }
              : result;
          },
          { overview: true },
        );
      }}
    >
      <label className={LABEL_CLASS} htmlFor="sponsor">
        {wording.nominative}
      </label>
      <select
        id="sponsor"
        className={INPUT_CLASS}
        value={sponsorId}
        disabled={locked}
        onChange={(event) => {
          setSponsorId(event.target.value);
        }}
      >
        <option value="">Bitte wählen</option>
        {choices.map((sponsor) => (
          <option key={sponsor.sponsor_id} value={sponsor.sponsor_id}>
            {sponsor.company_name}
          </option>
        ))}
      </select>
      <Button className="mt-4" type="submit" disabled={locked || !valid}>
        Belegen
      </Button>
    </form>
  );
}

/**
 * A booked door's invitation. The buttons stand only where the API acts on them: from the
 * go-live on (before it the mail would wait and the link open nothing), with the sponsors
 * permission.
 */
function Invitation({
  booking,
  actionId,
  locked,
  run,
}: {
  readonly booking: Booking;
  readonly actionId: string;
  readonly locked: boolean;
  readonly run: Run;
}): ReactElement {
  const position = booking.field_position;
  return (
    <Section title="Belegung">
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={locked}
          onClick={() => {
            run(() => sendInvitation(position, actionId));
          }}
        >
          Einladung verschicken
        </Button>
        <Button
          variant="outline"
          disabled={locked}
          onClick={() => {
            run(() => showInvitationLink(position, actionId));
          }}
        >
          Einladungslink anzeigen
        </Button>
      </div>
    </Section>
  );
}

/**
 * Releasing a booking, at the foot of the page with the other steps that take something away.
 * Hidden where `release_booking` refuses it: a door the public has seen is taken off with the
 * emergency stop, not released.
 */
function ReleaseBooking({
  booking,
  actionId,
  locked,
  run,
}: {
  readonly booking: Booking;
  readonly actionId: string;
  readonly locked: boolean;
  readonly run: Run;
}): ReactElement | null {
  const [reason, setReason] = useState('');
  const trimmedReason = reason.trim();
  if (RELEASE_BLOCKED.has(booking.workflow_state)) {
    return null;
  }
  return (
    <Section title="Belegung lösen" danger>
      <label className={LABEL_CLASS} htmlFor="loesegrund">
        Grund
        <Required />
      </label>
      <input
        id="loesegrund"
        className={INPUT_CLASS}
        aria-required="true"
        value={reason}
        maxLength={500}
        disabled={locked}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <Button
        className="mt-3"
        variant="outline"
        disabled={locked || trimmedReason.length < 3}
        onClick={() => {
          run(
            () =>
              releaseBooking(
                { field_position: booking.field_position, reason: trimmedReason },
                actionId,
              ),
            { overview: true },
          );
          setReason('');
        }}
      >
        Belegung lösen
      </Button>
    </Section>
  );
}

/** One figure of a door: a small label over its value, the value quiet while it is open. */
function Figure({
  label,
  value,
  open = false,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly open?: boolean;
}): ReactElement {
  return (
    <div className="min-w-0 rounded-md bg-surface-sunken px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={open ? 'mt-0.5 text-sm text-muted-foreground' : 'mt-0.5 text-sm font-medium'}>
        {value}
      </dd>
    </div>
  );
}

/**
 * What stands between this door and its day, from the booking the page already has: the way
 * of the door (contact, invitation, logo, release). Its content is {@link contentChecklist},
 * the column beside it (D94). The scan is no line: it never waits on anybody, a refused file
 * goes back to the sponsor at once, and a release it blocks says so where it is made.
 *
 * The publisher fills a door alone (D40): the page has to say what is still open instead of
 * leaving it to the sponsor's review steps. Release is the last line because it follows from
 * the others: on the own sponsor's doors a complete save with checked files releases the door,
 * on a real sponsor's the sponsor approves what the publisher made (H2), and the line says so.
 */
export function checklistOf(
  booking: Booking,
  invitationSent: boolean,
  now: Date,
  portalAccount = false,
  wording: Wording = SPONSOR_WORDING,
): readonly ChecklistItem[] {
  const due = booking.fallback_due_at ?? null;
  const late = due !== null && now.getTime() >= Date.parse(due);
  const released = isPublic(booking.workflow_state) || booking.workflow_state === 'finished';
  // A door the sponsor handed in is released with "Freigeben"; one they did not, or that went
  // to them, waits for their approval. A returned door is theirs to hand in again, unless the
  // publisher sent it or the sponsor answered a request.
  const handedIn =
    REVIEW_STATES.has(booking.workflow_state) ||
    (booking.submitted_at !== null &&
      booking.sent_to_sponsor_at == null &&
      booking.sponsor_comment == null);
  const sponsorApproves = booking.approval_required && !released && !handedIn;
  // The publisher designs: the sponsor still needs a way into the portal to approve, and the
  // access invitation lives on the sponsor's page.
  const access = booking.maintenance === 'publisher_designs';
  // An account gets the sponsor in as well, also one bound through a link handed over by hand.
  const reached = invitationSent || (access && portalAccount);
  // Only a sponsor who edits or delivers pictures is invited (H4); the own sponsor (H3) needs
  // no contact either.
  return [
    ...(booking.maintenance === 'publisher_only'
      ? []
      : [{ label: 'Ansprechpartner hinterlegt', done: booking.contact_configured === true }]),
    ...(booking.invites || access
      ? [
          {
            label: access ? wording.access : 'Einladung versendet',
            done: reached,
            ...(reached
              ? {}
              : {
                  detail: booking.invites
                    ? 'geht ab dem Livegang automatisch raus'
                    : `auf der Seite des ${wording.genitive}`,
                }),
          },
        ]
      : []),
    {
      label: 'Logo im Kalender',
      done: booking.logo_published === true,
      ...(booking.logo_published === true
        ? {}
        : { detail: `auf der Seite des ${wording.genitive}` }),
    },
    {
      label: sponsorApproves ? `Vom ${wording.oblique} freigegeben` : 'Freigegeben',
      done: released,
      ...(due === null ? {} : { detail: late ? 'Frist vorbei' : `bis ${formatDateTime(due)}` }),
    },
    // Applying it clears the correction, so while there is one it is not approved yet (E4).
    ...(booking.correction
      ? [{ label: `Korrektur vom ${wording.oblique} freigegeben`, done: false }]
      : []),
  ];
}

/** One column of the checklist: a tick or a cross, the label, what is missing after it. */
function ChecklistColumn({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly ChecklistItem[];
}): ReactElement {
  return (
    <div>
      <h4 className="text-sm text-muted-foreground">{title}</h4>
      <ul className="mt-1 grid gap-1 text-sm">
        {items.map((item) => (
          <li key={item.label} className="flex gap-2">
            <span aria-hidden="true" className={item.done ? 'text-success' : 'text-destructive'}>
              {item.done ? '✓' : '✗'}
            </span>
            <span>
              <span className="sr-only">{item.done ? 'Erfüllt: ' : 'Offen: '}</span>
              {item.label}
              {item.detail === undefined ? null : (
                <span className="text-muted-foreground">: {item.detail}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The booking's content in the three lines of the checklist (D94). */
export function bookingContent(booking: Booking): readonly ChecklistItem[] {
  return contentChecklist({
    slides: booking.slides.length,
    questions: booking.quiz.questions.length,
    prizeImage: booking.prize_image !== null,
    missing: booking.missing,
  });
}

/** Two columns (D94): the door's way on the left, its content on the right. */
function DoorChecklist({
  booking,
  field,
  wording,
}: {
  readonly booking: Booking;
  readonly field: FieldView;
  readonly wording: Wording;
}): ReactElement {
  const items = checklistOf(
    booking,
    field.invitation_sent,
    systemClock.now(),
    field.portal_account,
    wording,
  );
  return (
    <Section title="Checkliste">
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <ChecklistColumn title="Ablauf" items={items} />
        <ChecklistColumn title="Inhalt" items={bookingContent(booking)} />
      </div>
    </Section>
  );
}

/** A warning, nothing more: the door can still be edited and released (E7). */
export const DEADLINE_OVERDUE_NOTICE =
  'Die Freigabefrist ist abgelaufen. Bis zur Freigabe zeigt der Kalender den Ersatzinhalt.';

/**
 * What a save does to this door: released at once (D40), a correction (E4), or neither (H2).
 * `null` for the plain case, a door that a complete save releases: the save bar says that
 * quietly (`DOOR_SAVE_HINT`), a box would be noise (23.10.2026).
 */
function saveNotice(booking: Booking, wording: Wording): string | null {
  const state = booking.workflow_state;
  if (booking.stages_correction) {
    return REVIEW_STATES.has(state)
      ? `Änderungen werden als Korrekturfassung gespeichert, die der ${wording.nominative} freigibt. »Freigeben« gibt seine eingereichte Fassung frei.`
      : `Änderungen werden als Korrekturfassung gespeichert. Der Kalender zeigt bis zur Freigabe des ${wording.genitive} die bisherige Fassung.`;
  }
  if (booking.approval_required && OPEN_STATES.has(state)) {
    return `Speichern gibt nicht frei. Ist das Türchen fertig, senden Sie es dem ${wording.oblique} zur Freigabe.`;
  }
  if (isPublic(state)) {
    return 'Freigegeben: Änderungen sind sofort sichtbar.';
  }
  if (REVIEW_STATES.has(state)) {
    return `Der ${wording.nominative} hat eingereicht. Speichern gibt den ganzen Beitrag frei.`;
  }
  return null;
}

/** Beside the bar's "Speichern" while the editor has nothing above it to say. */
export const DOOR_SAVE_HINT = 'Gibt frei, sobald vollständig.';

/**
 * The line above the editor; an open request comes first, since any save takes it back. Then
 * only a correction needs saying: the call to send what was just sent would be noise.
 */
export function editorNotice(booking: Booking, wording: Wording = SPONSOR_WORDING): string | null {
  if (booking.sent_to_sponsor_at == null) {
    return saveNotice(booking, wording);
  }
  const request = `Liegt dem ${wording.oblique} zur Freigabe vor. Speichern zieht die Anfrage zurück.`;
  return booking.stages_correction ? `${request} ${saveNotice(booking, wording) ?? ''}` : request;
}

/**
 * Why "An den Sponsor zur Freigabe senden" is locked by the files of the version it sends;
 * `null` when they pass. Missing parts lock it as well, the "Fehlt noch" line under the door
 * names them.
 */
/** `blocked` names what the page offers: "Senden", "Veröffentlichen" or both. */
export function sendScanNotice(booking: Booking, blocked: string): string | null {
  const files = booking.send_scan_status ?? null;
  return files === null || files === 'clean'
    ? null
    : `Eine Datei des Beitrags ist nicht geprüft. ${blocked} gesperrt.`;
}

/**
 * The contribution, with the steps its current state allows.
 *
 * The buttons follow the state machine of concept 9.7.2 rather than being always
 * present and sometimes refused: `submitted` can be opened, `in_review` can be
 * released or sent back. The server decides either way; this only avoids offering a
 * click whose answer is already known to be 409.
 *
 * The publisher edits the content in every state (D37), also after the submission and the
 * release; the state stays, and the audit row keeps the door before and after. Only an
 * ended action is shown and not changed (`writable`).
 *
 * An image the scan has not cleared blocks "Freigeben". Only the tenant admin gets the
 * exception, with a reason that goes into the audit log. An unscanned logo blocks nothing,
 * but only that exception publishes it, so it offers the button as well.
 *
 * On the page stand the deadlines and one step, »Türchen bearbeiten«, or »Türchen freigeben«
 * where the sponsor handed something in (D94). The door and every step of its decision open in
 * a dialog, the decision under the door. The dialog takes the page's save bar
 * (`SaveBarSlot`), so »Speichern« and its report stand there too. Closing saves first and
 * closes only when that worked; after a failed save it offers to close without. The editor is
 * mounted while the dialog is open: what was not saved goes with it, which the save on closing
 * is there to prevent. The door's sponsor switch stays on the page, it is no part of the door.
 */
export function Contribution({
  booking,
  actionId,
  mayReview,
  mayAcceptUnscanned,
  locked,
  run,
  writable = true,
  layoutAllowed = false,
  sponsorWorkflow = false,
  live = false,
  customer = false,
  openAtStart = false,
}: {
  readonly booking: Booking;
  readonly actionId: string;
  /**
   * May start a review, release, return, withdraw a release or send to the sponsor (the
   * element of the review).
   */
  readonly mayReview: boolean;
  /** May release with "Ungeprüft übernehmen" (its own element). */
  readonly mayAcceptUnscanned: boolean;
  readonly locked: boolean;
  readonly run: Run;
  /** The action has not ended: the content can be changed. */
  readonly writable?: boolean;
  /** May set the door's own layout (the element of the door's layout). */
  readonly layoutAllowed?: boolean;
  /** Shows the door's sponsor switch: part of »Pflege durch Sponsoren« (D57). */
  readonly sponsorWorkflow?: boolean;
  /** The action is live or paused: the portal answers, so a door can go to the sponsor. */
  readonly live?: boolean;
  /**
   * A customer's calendar (E5): no approval way, so no deadlines and none of its steps
   * (send, remind, publish without approval, withdraw a release).
   */
  readonly customer?: boolean;
  /** Open the dialog with the page (`?bearbeiten`, the plan's open tasks). */
  readonly openAtStart?: boolean;
}): ReactElement {
  // »Sponsor« or »Kunde« (E8). The steps only a sponsor's door offers keep their word.
  const wording = customer ? CUSTOMER_WORDING : SPONSOR_WORDING;
  const [comment, setComment] = useState('');
  const [silent, setSilent] = useState(false);
  const [quietSend, setQuietSend] = useState(false);
  const [unscannedReason, setUnscannedReason] = useState('');
  const [direct, setDirect] = useState(false);
  const [directReason, setDirectReason] = useState('');
  const [open, setOpen] = useState(openAtStart && writable);
  // A close whose save failed: the dialog stays and offers to close without saving.
  const [unsaved, setUnsaved] = useState(false);
  const saveFirst = useSaveFirst();
  // A release, a send or a publication works on what is stored: not while the editor holds more.
  const dirty = useSaveBarDirty();
  const layout = useDoorLayout(booking, actionId, run, layoutAllowed);
  const canReview = mayReview && booking.workflow_state === 'submitted';
  // A staged correction has to be sent, published or discarded first (`CorrectionPendingError`).
  const canDecide = mayReview && booking.workflow_state === 'in_review' && !booking.correction;
  // A release whose copy into the public area failed stands; releasing again repeats the copy.
  const releasable = booking.workflow_state === 'in_review' || booking.publication_pending;
  const canRelease = mayReview && releasable;
  // Also after the deadline: it decides nothing any more (E7).
  const canWithdraw =
    mayReview && !customer && booking.workflow_state === 'approved' && !booking.correction;
  // Where `send_booking_to_sponsor` takes it (H2): a real sponsor's door from the go-live on,
  // its correction, or the row the sponsor never handed in. Sending again replaces the request.
  const canSend =
    mayReview &&
    !customer &&
    live &&
    booking.approval_required &&
    (booking.correction || SENDABLE_ROW_STATES.has(booking.workflow_state));
  // `publish_booking_without_sponsor` (E3): the version a send would take, or any staged
  // correction short of the emergency stop; on the own sponsor's doors only a correction left.
  const canPublishDirect =
    mayReview &&
    !customer &&
    writable &&
    (booking.correction
      ? booking.workflow_state !== 'depublished' && booking.workflow_state !== 'invited'
      : booking.approval_required && SENDABLE_ROW_STATES.has(booking.workflow_state));
  const sendFiles =
    canSend || canPublishDirect
      ? sendScanNotice(
          booking,
          canSend && canPublishDirect
            ? 'Senden und Veröffentlichen'
            : canSend
              ? 'Senden'
              : 'Veröffentlichen',
        )
      : null;
  const versionBlocked = booking.missing.length > 0 || sendFiles !== null;
  const trimmedDirectReason = directReason.trim();
  // Without a contact address no mail can go; the request stands in the portal all the same.
  const notify = booking.contact_configured === true && !quietSend;
  const pending = booking.sent_to_sponsor_at ?? null;
  // E5: by hand only, and only where a mail can go.
  const canRemind =
    mayReview &&
    !customer &&
    writable &&
    pending !== null &&
    booking.contact_configured === true;
  const discardLabel = booking.correction ? 'Korrektur verwerfen' : 'Anfrage zurückziehen';
  // Only while there is a release to make: after it the verdicts stay on the booking, and
  // "Freigabe gesperrt" under a door that is already released is wrong.
  const scanBlock = canRelease ? scanNotice(booking.image_scan_status) : null;
  const logoWaits = canRelease ? logoNotice(booking.logo_scan_status) : null;
  // The emergency permission alone, as on the server: not tied to the review permission.
  const canAcceptUnscanned =
    releasable &&
    mayAcceptUnscanned &&
    acceptsUnscanned(booking.image_scan_status, booking.logo_scan_status);
  const trimmedUnscannedReason = unscannedReason.trim();
  const position = booking.field_position;

  const editorial = (path: Parameters<typeof runEditorialAction>[0]): void => {
    run(() => runEditorialAction(path, { field_position: position }, actionId));
  };

  const close = (): void => {
    void saveFirst().then((saved) => {
      setUnsaved(!saved);
      if (saved) {
        setOpen(false);
      }
    });
  };
  const discard = (): void => {
    layout.reset();
    setUnsaved(false);
    setOpen(false);
  };
  // Something of the sponsor's waits for the decision.
  const decision = canReview || canDecide;

  const page = (action: ActionDoorWindow | null): ReactElement => (
    <Section title="Beitrag">
      {customer ? null : (
        <dl className="mt-3 flex flex-wrap gap-2">
          <Figure
            label="Einreichfrist"
            value={deadline(booking.submission_deadline)}
            open={booking.submission_deadline === null}
          />
          <Figure
            label="Freigabefrist"
            value={deadline(booking.approval_deadline)}
            open={booking.approval_deadline === null}
          />
        </dl>
      )}

      {layoutAllowed && sponsorWorkflow ? (
        <SponsorLayoutSwitch booking={booking} action={action} layout={layout} />
      ) : null}

      {writable ? (
        <Button
          className="mt-5"
          variant={decision ? 'default' : 'outline'}
          disabled={locked}
          onClick={() => {
            setOpen(true);
          }}
        >
          {decision ? 'Türchen freigeben' : 'Türchen bearbeiten'}
        </Button>
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setOpen(true);
          } else {
            close();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className="top-4 right-4 bottom-4 left-4 w-auto max-w-none translate-x-0 translate-y-0 content-start overflow-y-auto rounded-none sm:max-w-none"
          // Escape in the crop dialog over the door closes that one, not the door.
          onEscapeKeyDown={(event) => {
            if (document.querySelector('dialog[open]') !== null) {
              event.preventDefault();
            }
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>Türchen {position}</DialogTitle>
            <Button variant="outline" onClick={close}>
              Schließen
            </Button>
          </div>
          {open ? (
            <BookingDoorEditor
              booking={booking}
              actionId={actionId}
              run={run}
              layout={layout}
              layoutAllowed={layoutAllowed}
              action={action}
              wording={wording}
              onInvalid={() => {
                setOpen(true);
              }}
            />
          ) : null}
          <div className="border-t border-border pt-4">
            {booking.missing.length > 0 ? (
              <p className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">Fehlt noch:</span>
                {booking.missing.map((key) => (
                  <StatusBadge key={key} tone="warning">
                    {missingLabel(key)}
                  </StatusBadge>
                ))}
              </p>
            ) : null}

            {booking.review_comment !== null ? (
              <p className="mt-3 text-sm">
                <span className="text-muted-foreground">Letzte Rückmeldung: </span>
                {booking.review_comment}
              </p>
            ) : null}

            {pending !== null ? (
              <p className="mt-3 text-sm">
                Beim {wording.oblique} seit {formatDateTime(pending)}
              </p>
            ) : null}

            {booking.sponsor_comment != null ? (
              <p className="mt-3 text-sm">
                <span className="text-muted-foreground">Änderungswunsch des {wording.genitive}: </span>
                {booking.sponsor_comment}
              </p>
            ) : null}

            {booking.deadline_overdue ? (
              <p className="mt-3 rounded-md bg-surface-sunken px-4 py-3 text-sm">
                {DEADLINE_OVERDUE_NOTICE}
              </p>
            ) : null}

            {scanBlock !== null ? (
              <p className="mt-3 rounded-md bg-surface-sunken px-4 py-3 text-sm">{scanBlock}</p>
            ) : null}

            {logoWaits !== null ? (
              <p className="mt-3 rounded-md bg-surface-sunken px-4 py-3 text-sm">{logoWaits}</p>
            ) : null}

            {sendFiles !== null ? (
              <p className="mt-3 rounded-md bg-surface-sunken px-4 py-3 text-sm">{sendFiles}</p>
            ) : null}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {canReview ? (
                <Button
                  disabled={locked}
                  onClick={() => {
                    editorial('/api/v1/backoffice/bookings/review');
                  }}
                >
                  Prüfung beginnen
                </Button>
              ) : null}

              {canRelease ? (
                <Button
                  disabled={locked || scanBlock !== null || dirty}
                  onClick={() => {
                    editorial('/api/v1/backoffice/bookings/approve');
                  }}
                >
                  {/* The row, not the correction: that one goes to the sponsor (E4). */}
                  {booking.correction && booking.workflow_state === 'in_review'
                    ? 'Eingereichte Fassung freigeben'
                    : 'Freigeben'}
                </Button>
              ) : null}

              {canSend ? (
                <Button
                  disabled={locked || versionBlocked || dirty}
                  onClick={() => {
                    run(
                      async () => {
                        const result = await runEditorialAction(
                          '/api/v1/backoffice/bookings/send-to-sponsor',
                          { field_position: position, notify },
                          actionId,
                        );
                        return result.ok
                          ? {
                              ok: true,
                              message: notify
                                ? 'Zur Freigabe an den Sponsor gesendet.'
                                : 'Zur Freigabe an den Sponsor gesendet (ohne Benachrichtigung).',
                            }
                          : result;
                      },
                      {
                        onSuccess: () => {
                          setQuietSend(false);
                        },
                      },
                    );
                  }}
                >
                  An den Sponsor zur Freigabe senden
                </Button>
              ) : null}

              {canRemind ? (
                <Button
                  variant="outline"
                  disabled={locked}
                  onClick={() => {
                    run(async () => {
                      const result = await runEditorialAction(
                        '/api/v1/backoffice/bookings/remind-sponsor',
                        { field_position: position },
                        actionId,
                      );
                      return result.ok
                        ? { ok: true, message: 'Erinnerung an den Sponsor gesendet.' }
                        : result;
                    });
                  }}
                >
                  Erinnerung senden
                </Button>
              ) : null}

              {canPublishDirect ? (
                <Button
                  variant="outline"
                  disabled={locked || versionBlocked || dirty}
                  aria-expanded={direct}
                  aria-controls="direkt-veroeffentlichen"
                  onClick={() => {
                    setDirect(!direct);
                  }}
                >
                  Direkt veröffentlichen …
                </Button>
              ) : null}

              {canWithdraw ? (
                <Button
                  variant="outline"
                  disabled={locked}
                  onClick={() => {
                    editorial('/api/v1/backoffice/bookings/withdraw-approval');
                  }}
                >
                  Freigabe zurücknehmen
                </Button>
              ) : null}

              {/* The doors permission, as on the server: the booking list needs it, so whoever sees
                  this block may write the correction and throw it away. */}
              {writable && (booking.correction || pending !== null) ? (
                <ConfirmButton
                  label={discardLabel}
                  question={`${discardLabel}?`}
                  consequence={
                    booking.correction
                      ? 'Die Korrekturfassung wird gelöscht. Das Türchen behält die bisherige Fassung.'
                      : `Der ${wording.nominative} kann diese Fassung dann nicht mehr freigeben.`
                  }
                  confirmLabel={discardLabel}
                  disabled={locked}
                  // The dialog only asks; the page runs the write like every other one, so the
                  // buttons lock meanwhile and the outcome stands where the others do.
                  run={() => {
                    const done = booking.correction ? 'Korrektur verworfen.' : 'Anfrage zurückgezogen.';
                    run(async () => {
                      const result = await runEditorialAction(
                        '/api/v1/backoffice/bookings/discard-correction',
                        { field_position: position },
                        actionId,
                      );
                      return result.ok ? { ok: true, message: done } : result;
                    });
                    return Promise.resolve({ ok: true });
                  }}
                  onDone={() => undefined}
                />
              ) : null}
            </div>

            {canSend && booking.contact_configured === true ? (
              <div className="mt-2 flex items-center gap-2">
                <input
                  id="still-senden"
                  className="border border-input"
                  type="checkbox"
                  checked={quietSend}
                  disabled={locked}
                  onChange={(event) => {
                    setQuietSend(event.target.checked);
                  }}
                />
                <label className="text-sm" htmlFor="still-senden">
                  Sponsor nicht benachrichtigen
                </label>
              </div>
            ) : null}

            {canPublishDirect && direct && !versionBlocked ? (
              <div id="direkt-veroeffentlichen" className="mt-3">
                <p id="direkt-hinweis" className="text-sm">
                  Der Sponsor hat diese Fassung nicht freigegeben. Die Begründung wird protokolliert.
                </p>
                <label className={LABEL_CLASS} htmlFor="direkt-begruendung">
                  Begründung
                </label>
                <input
                  id="direkt-begruendung"
                  className={INPUT_CLASS}
                  aria-describedby="direkt-hinweis"
                  required
                  minLength={3}
                  value={directReason}
                  maxLength={500}
                  disabled={locked}
                  onChange={(event) => {
                    setDirectReason(event.target.value);
                  }}
                />
                <Button
                  className="mt-3"
                  disabled={locked || trimmedDirectReason.length < 3}
                  onClick={() => {
                    run(
                      async () => {
                        const result = await runEditorialAction(
                          '/api/v1/backoffice/bookings/publish-without-sponsor',
                          { field_position: position, reason: trimmedDirectReason },
                          actionId,
                        );
                        return result.ok
                          ? { ok: true, message: 'Ohne Freigabe des Sponsors veröffentlicht.' }
                          : result;
                      },
                      {
                        onSuccess: () => {
                          setDirect(false);
                          setDirectReason('');
                        },
                      },
                    );
                  }}
                >
                  Veröffentlichen
                </Button>
              </div>
            ) : null}

            {canAcceptUnscanned ? (
              <div className="mt-3">
                <label className={LABEL_CLASS} htmlFor="ungeprueft-begruendung">
                  Begründung
                  <Required />
                </label>
                <input
                  id="ungeprueft-begruendung"
                  className={INPUT_CLASS}
                  aria-required="true"
                  value={unscannedReason}
                  maxLength={500}
                  disabled={locked}
                  onChange={(event) => {
                    setUnscannedReason(event.target.value);
                  }}
                />
                <Button
                  className="mt-3"
                  variant="outline"
                  disabled={locked || trimmedUnscannedReason.length < 3}
                  onClick={() => {
                    run(() => approveUnscanned(position, trimmedUnscannedReason, actionId), {
                      onSuccess: () => {
                        setUnscannedReason('');
                      },
                    });
                  }}
                >
                  Ungeprüft freigeben
                </Button>
              </div>
            ) : null}

            {canDecide ? (
              <div className="mt-3">
                <label className="block text-sm" htmlFor="kommentar">
                  Kommentar an den {wording.oblique}
                </label>
                <div className="mt-1 flex flex-wrap gap-2">
                  <input
                    id="kommentar"
                    className="min-w-0 flex-1 border border-input bg-background px-3 py-2 text-sm"
                    value={comment}
                    onChange={(event) => {
                      setComment(event.target.value);
                    }}
                  />
                  <Button
                    variant="outline"
                    disabled={locked || comment.trim() === ''}
                    onClick={() => {
                      run(() =>
                        runEditorialAction(
                          '/api/v1/backoffice/bookings/request-changes',
                          { field_position: position, comment, notify: !silent },
                          actionId,
                        ),
                      );
                      setComment('');
                      setSilent(false);
                    }}
                  >
                    Zurückgeben
                  </Button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="still-zurueckgeben"
                    className="border border-input"
                    type="checkbox"
                    checked={silent}
                    disabled={locked}
                    onChange={(event) => {
                      setSilent(event.target.checked);
                    }}
                  />
                  <label className="text-sm" htmlFor="still-zurueckgeben">
                    {wording.nominative} nicht benachrichtigen
                  </label>
                </div>
              </div>
            ) : null}
          </div>
          <SaveBarSlot />
          {unsaved ? (
            <p className="text-sm">
              Nicht gespeichert.{' '}
              <Button variant="link" className="h-auto p-0" onClick={discard}>
                Ohne Speichern schließen
              </Button>
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </Section>
  );

  return layoutAllowed ? <ActionLayout actionId={actionId}>{page}</ActionLayout> : page(null);
}

/** What a door inherits from its action (D37): the standard layout and the sponsor switch. */
interface ActionDoorWindow {
  readonly layout: DoorWindowLayout;
  readonly sponsorEditable: boolean;
}

/**
 * The action's layout for the door's editor, for the roles that may read the action's
 * settings. Until it is loaded, and where it cannot be, the editor works without it.
 */
function ActionLayout({
  actionId,
  children,
}: {
  readonly actionId: string;
  readonly children: (action: ActionDoorWindow | null) => ReactNode;
}): ReactNode {
  const load = useCallback(
    (api: CountdownApiClient) => loadActionDetail(api, actionId),
    [actionId],
  );
  const detail = useResource<ActionDetail>(load);
  return children(
    detail.status === 'ready'
      ? {
          layout: detail.data.door_layout ?? DEFAULT_DOOR_LAYOUT,
          sponsorEditable: detail.data.door_layout_sponsor_editable ?? false,
        }
      : null,
  );
}

/** A door's own layout and sponsor switch as stored; `null` follows the action. */
interface DoorLayoutDraft {
  readonly own: FullDoorLayout | null;
  readonly sponsor: boolean | null;
}

/**
 * The draft as stored, fitted to the presentation: an own layout kept from the page may hold
 * a format the window over the calendar refuses, and would fail every later save.
 */
function layoutDraftOf(booking: Booking): DoorLayoutDraft {
  return {
    own: booking.own_door_layout === null || booking.own_door_layout === undefined
      ? null
      : fitLayout(fullLayout(booking.own_door_layout), booking.door_presentation ?? 'page'),
    sponsor: booking.own_door_layout_sponsor_editable ?? null,
  };
}

function sameDraft(left: DoorLayoutDraft, right: DoorLayoutDraft): boolean {
  return sameLayout(left.own, right.own) && left.sponsor === right.sponsor;
}

/** The sponsor switch of one door: the action's, yes or no. */
const SPONSOR_CHOICES = [
  ['', 'Wie die Aktion'],
  ['ja', 'Ja'],
  ['nein', 'Nein'],
] as const;

/** The door's layout as typed, the stored one, and the write that stores it. */
interface DoorLayoutState {
  readonly draft: DoorLayoutDraft;
  readonly change: (update: (current: DoorLayoutDraft) => DoorLayoutDraft) => void;
  readonly dirty: boolean;
  readonly busy: boolean;
  readonly notice: string | null;
  /** The page's save bar took the section: no button of its own. */
  readonly inBar: boolean;
  readonly save: () => Promise<SaveResult>;
  /** Back to the stored layout, for a dialog closed without saving. */
  readonly reset: () => void;
}

/**
 * The door's own layout and sponsor switch, held by the page's part of the contribution: the
 * switch stands on the page, the layout in the dialog (D94), and one write stores both as the
 * bar's section »Aufbau«, for the roles that set the action's layout as well.
 */
function useDoorLayout(
  booking: Booking,
  actionId: string,
  run: Run,
  layoutAllowed: boolean,
): DoorLayoutState {
  const position = booking.field_position;
  const [draft, setDraft] = useState<DoorLayoutDraft>(() => layoutDraftOf(booking));
  const [saved, setSaved] = useState<DoorLayoutDraft>(draft);
  const [seen, setSeen] = useState(booking);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = !sameDraft(draft, saved);

  // A reload after any write brings the stored layout; a draft nobody touched follows it.
  if (booking !== seen) {
    setSeen(booking);
    const stored = layoutDraftOf(booking);
    setSaved(stored);
    if (!dirty) {
      setDraft(stored);
    }
  }

  async function save(): Promise<SaveResult> {
    setBusy(true);
    setNotice(null);
    try {
      const outcome = await saveFieldDoorLayout(actionId, {
        field_position: position,
        layout: draft.own === null ? null : layoutRequest(draft.own),
        sponsor_editable: draft.sponsor,
      });
      if (!outcome.ok) {
        setNotice(outcome.message);
        return 'failed';
      }
      console.log('[DoorPage] door layout saved', position);
      const stored = layoutDraftOf(outcome.booking);
      setSaved(stored);
      setDraft(stored);
      run(() => Promise.resolve({ ok: true, message: 'Aufbau gespeichert.' }));
      return 'ok';
    } finally {
      setBusy(false);
    }
  }

  const inBar = useSaveSection(layoutAllowed ? { label: 'Aufbau', dirty, save } : null);

  return {
    draft,
    change: setDraft,
    dirty,
    busy,
    notice,
    inBar,
    save,
    reset: () => {
      setDraft(saved);
      setNotice(null);
    },
  };
}

/** The action's layout, or what the booking shows while it follows the action. */
function inheritedLayout(booking: Booking, action: ActionDoorWindow | null): DoorWindowLayout {
  return (
    action?.layout ??
    (booking.own_door_layout === null || booking.own_door_layout === undefined
      ? booking.door_layout
      : null) ??
    DEFAULT_DOOR_LAYOUT
  );
}

/**
 * »Sponsor darf den Aufbau ändern« for one door, on the page beside the door's step (D94): a
 * setting about the door, not part of it. A door without its own answer follows its sponsor's
 * (E6), else the action's; the first choice names which, with its answer.
 */
function SponsorLayoutSwitch({
  booking,
  action,
  layout,
}: {
  readonly booking: Booking;
  readonly action: ActionDoorWindow | null;
  readonly layout: DoorLayoutState;
}): ReactElement {
  const position = booking.field_position;
  const sponsorOwn = booking.sponsor_layout ?? null;
  const inheritedSponsor =
    sponsorOwn ??
    action?.sponsorEditable ??
    (booking.own_door_layout_sponsor_editable === null ||
    booking.own_door_layout_sponsor_editable === undefined
      ? booking.door_layout_sponsor_editable
      : null);
  const sponsorSource = sponsorOwn === null ? 'Wie die Aktion' : 'Wie beim Sponsor';
  const sponsorDefault =
    inheritedSponsor === null ? sponsorSource : `${sponsorSource} (${inheritedSponsor ? 'ja' : 'nein'})`;
  return (
    <div className="mt-3 sm:max-w-xs">
      <label className={LABEL_CLASS} htmlFor={`tuer-${String(position)}-sponsor-aufbau`}>
        Sponsor darf den Aufbau ändern
      </label>
      <select
        id={`tuer-${String(position)}-sponsor-aufbau`}
        className={INPUT_CLASS}
        disabled={layout.busy}
        value={layout.draft.sponsor === null ? '' : layout.draft.sponsor ? 'ja' : 'nein'}
        onChange={(event) => {
          const value = event.target.value;
          layout.change((current) => ({
            ...current,
            sponsor: value === '' ? null : value === 'ja',
          }));
        }}
      >
        {SPONSOR_CHOICES.map(([value, label]) => (
          <option key={value} value={value}>
            {value === '' ? sponsorDefault : label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * The door's configurator (D37) in the dialog, with the door's own layout above it.
 *
 * Content and layout are two writes with their own sections in the save bar: the content
 * (»Beitrag«) for every role of the review, the layout (»Aufbau«, `useDoorLayout`) for the roles
 * that set the action's layout as well. Without an own layout the window shows the action's,
 * read only; »Eigener Aufbau für dieses Türchen« copies it to change it here.
 */
function BookingDoorEditor({
  booking,
  actionId,
  run,
  layout,
  layoutAllowed,
  action,
  wording,
  onInvalid,
}: {
  readonly booking: Booking;
  readonly actionId: string;
  readonly run: Run;
  readonly layout: DoorLayoutState;
  readonly layoutAllowed: boolean;
  readonly action: ActionDoorWindow | null;
  readonly wording: Wording;
  readonly onInvalid: () => void;
}): ReactElement {
  const position = booking.field_position;
  const { draft } = layout;
  const inherited = inheritedLayout(booking, action);
  const editorSays = editorNotice(booking, wording);

  return (
    <div>
      {layoutAllowed ? (
        <fieldset className="mb-3 min-w-0" disabled={layout.busy}>
          <legend className="sr-only">Aufbau dieses Türchens</legend>
          <div className="flex items-start gap-2">
            <input
              id={`tuer-${String(position)}-eigener-aufbau`}
              className="mt-1 border border-input"
              type="checkbox"
              checked={draft.own !== null}
              onChange={(event) => {
                const own = event.target.checked;
                layout.change((current) => ({
                  ...current,
                  own: own
                    ? fitLayout(fullLayout(inherited), booking.door_presentation ?? 'page')
                    : null,
                }));
              }}
            />
            <label className="text-sm font-medium" htmlFor={`tuer-${String(position)}-eigener-aufbau`}>
              Eigener Aufbau für dieses Türchen
            </label>
            <HelpTip topic="Eigener Aufbau">{HELP_TIPS.ownLayout}</HelpTip>
          </div>
          {layout.inBar ? null : (
            <Button
              className="mt-3"
              type="button"
              variant="outline"
              disabled={!layout.dirty}
              onClick={() => {
                void layout.save();
              }}
            >
              Aufbau speichern
            </Button>
          )}
          <div role="status">
            {layout.notice === null ? null : (
              <p className="mt-2 text-sm text-destructive">{layout.notice}</p>
            )}
          </div>
        </fieldset>
      ) : null}
      <DoorEditor
        previewTitle={`Vorschau des Fensters von Türchen ${String(position)}`}
        door={booking}
        writes={{
          // On the action of this page: a draft's door is saved on the draft.
          save: (request) => saveBookingDocument(request, actionId),
          upload: ({ slot, file, altText, crop }) =>
            uploadBookingMedia(
              { position, slot, file, altText, ...(crop === undefined ? {} : { crop }) },
              actionId,
            ),
          recut: ({ slot, assetId, crop }) =>
            uploadBookingMedia({ position, slot, assetId, crop }, actionId),
          original: originalAddress,
          picture: assetAddress,
        }}
        onSaved={(outcome) => {
          run(() => Promise.resolve(outcome));
        }}
        onInvalid={onInvalid}
        layout={draft.own ?? inherited}
        {...(layoutAllowed && draft.own !== null
          ? {
              onLayoutChange: (own: FullDoorLayout) => {
                layout.change((current) => ({ ...current, own }));
              },
            }
          : {})}
        // Following the action, the layout cannot be changed here: its settings are left out.
        hideLockedLayout
        presentation={booking.door_presentation ?? 'page'}
        {...(editorSays === null ? { saveHint: DOOR_SAVE_HINT } : { notice: editorSays })}
      />
    </div>
  );
}

/**
 * One door off the calendar and back (concept 9.6.4, level 2), for the tenant admin only.
 *
 * The reason sits next to the one button it belongs to. On the emergency page one reason
 * served every switch of an incident; here the door is already chosen, so there is only ever
 * one switch to throw.
 */
function EmergencyStop({
  field,
  actionId,
  locked,
  run,
}: {
  readonly field: FieldView;
  readonly actionId: string;
  readonly locked: boolean;
  readonly run: Run;
}): ReactElement {
  const [reason, setReason] = useState('');
  const trimmedReason = reason.trim();

  return (
    <Section title="Not-Aus" danger>
      <label className={LABEL_CLASS} htmlFor="notaus-begruendung">
        Begründung
        <Required />
      </label>
      <input
        id="notaus-begruendung"
        className={INPUT_CLASS}
        aria-required="true"
        value={reason}
        maxLength={500}
        disabled={locked}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <Button
        className="mt-3"
        variant={field.depublished ? 'outline' : 'destructive'}
        disabled={locked || trimmedReason.length < 3}
        onClick={() => {
          run(() =>
            pullEmergencySwitch(
              field.depublished ? 'republish-field' : 'depublish-field',
              { reason: trimmedReason, field_position: field.position },
              actionId,
            ),
          );
          setReason('');
        }}
      >
        {field.depublished ? 'Türchen wieder freigeben' : 'Türchen vom Netz nehmen'}
      </Button>
    </Section>
  );
}

interface Figure {
  readonly label: string;
  readonly value: number;
}

/** Label and number per row; the label is the row's header for a screen reader. */
function Figures({
  caption,
  rows,
}: {
  readonly caption: string;
  readonly rows: readonly Figure[];
}): ReactElement {
  return (
    <Table className="mt-2">
      <TableCaption className="sr-only">{caption}</TableCaption>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableHead scope="row" className="whitespace-normal font-normal">
              {row.label}
            </TableHead>
            <TableCell className="text-right align-middle tabular-nums">
              {NUMBER_FORMAT.format(row.value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * The door's figures (concept 8.3): the counters of its row in the plan. Counts without a
 * person behind them (8.4): there are no unique visitors, by decision (P8). The arrivals per
 * channel stay on the server until channels are told apart (23.10.2026).
 */
function DoorStatistics({ field }: { readonly field: FieldView }): ReactElement {
  return (
    <Section title="Statistik">
      <Figures
        caption={`Zahlen von Türchen ${field.position}`}
        rows={[
          { label: 'Aufrufe', value: field.views },
          // Drafts, as in the plan and the sponsor's report: a quiz played again counts once.
          { label: 'Antworten', value: field.drafts },
          { label: 'Begonnene Registrierungen', value: field.registrations_started },
          { label: 'Teilnahmen', value: field.participations },
          { label: 'Leads', value: field.leads },
        ]}
      />
    </Section>
  );
}

/**
 * One door (`/aktionen/:actionId/tuerchen/:position`): every step anybody takes on it.
 *
 * WHAT THIS REPLACES. Placing a sponsor, inviting and releasing sat on the sponsor screen
 * behind a typed field number, the review on a list of its own, and taking a door off on the
 * emergency screen behind another typed number. All of them act on one door of one action, so
 * they live on that door's page, and every call names the action by its id: the page of a
 * draft writes to the draft, never to whatever is live.
 *
 * The field comes from the action page, the booking from the booking list of this action.
 * After each write both reload, and the overview too when the header's count or the sponsor
 * list changes. A reload keeps the page mounted (`useResource`), so the write's message stays.
 *
 * An ended action is shown, not changed: its rows are the record of a season and the API
 * refuses every write to it, so the page offers none.
 *
 * The preview belongs to the door, not to its booking: 9.7.5 wants the release made on the
 * page the participants see, and an unbooked door opens with the fallback, which is worth a
 * look as well. It frames the participant page of this door on its own day, always beside the
 * door's settings, and loads again after every write that changes the door.
 */
/** The action as the two rules below read it. */
type CalendarKind = { readonly customer_sponsor_id?: string | null };

/** Who a free door can be given to: in a customer's calendar only the customer (E5). */
export function placeableSponsors(
  sponsors: readonly SponsorView[],
  action: CalendarKind,
): readonly SponsorView[] {
  const customer = action.customer_sponsor_id ?? null;
  return customer === null ? sponsors : sponsors.filter((sponsor) => sponsor.sponsor_id === customer);
}

/** Whether the door's switch »Sponsor darf den Aufbau ändern« shows: never in a customer's
 * calendar, whose customer keeps to the content (E5). */
export function doorSponsorWorkflow(granted: readonly string[], action: CalendarKind): boolean {
  return can(granted, 'sponsor_workflow') && (action.customer_sponsor_id ?? null) === null;
}

export function DoorPage(): ReactElement {
  const { action, fields, reloadFields, reloadOverview, sponsors, granted } = useActionContext();
  const { position } = useParams();
  const [search] = useSearchParams();
  const field = fields.find((entry) => String(entry.position) === position);
  const load = useCallback(
    (api: CountdownApiClient) => loadEditorialBookings(api, action.action_id),
    [action.action_id],
  );
  const bookings = useResource<BookingList>(load);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  // Bumped by every write that changes the door, so the preview loads again and shows it.
  const [revision, setRevision] = useState(0);
  const reloadBookings = bookings.reload;

  const run = useCallback<Run>(
    (write, options) => {
      setBusy(true);
      setOutcome(null);
      write()
        .then((result) => {
          setOutcome(result);
          // The invitation link changes nothing. Everything else is reloaded rather than
          // patched: a write moves the plan's row, the booking and the deadlines, and a
          // hand-patched copy would drift from the server's answer. A refused write is
          // reloaded too: the 502 of a release says the release stands although the copy failed.
          if (result.ok) {
            options?.onSuccess?.();
          }
          if (!result.ok || result.inviteUrl === undefined) {
            setRevision((value) => value + 1);
            reloadFields();
            reloadBookings();
            if (options?.overview === true) {
              reloadOverview();
            }
          }
        })
        .catch((error: unknown) => {
          console.error('[DoorPage] write failed', error);
          setOutcome({ ok: false, message: 'Der Schritt konnte nicht ausgeführt werden.' });
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [reloadFields, reloadBookings, reloadOverview],
  );

  if (field === undefined) {
    return <p className="text-sm text-muted-foreground">Türchen nicht gefunden.</p>;
  }

  const writable = action.status !== 'finished' && action.status !== 'archived';
  const locked = busy || bookings.pending;
  const booking =
    bookings.status === 'ready'
      ? (bookings.data.bookings.find((entry) => entry.field_position === field.position) ??
        null)
      : null;
  // The server's rule (`invitable`): a running action, or the customer's own draft (E6).
  const invitable = booking?.invitable === true && can(granted, 'door_invitation');
  // A customer's calendar (E5): only the customer is placed, and nobody approves.
  const customer = (action.customer_sponsor_id ?? null) !== null;
  const wording = wordingOf(action);
  const sponsorId = field.sponsor_id ?? null;
  // Somebody of the company has had an invitation or is in (D34).
  const reached = field.invitation_sent || field.portal_account;

  let sections: ReactNode;
  if (bookings.status !== 'ready') {
    sections = <p className="mt-6 text-sm text-muted-foreground">{unavailable(bookings.status)}</p>;
  } else if (booking === null) {
    sections = writable && can(granted, 'door_placement') ? (
      <Section title="Belegung">
        <PlacementForm
          position={field.position}
          actionId={action.action_id}
          sponsors={placeableSponsors(sponsors, action)}
          locked={locked}
          run={run}
          wording={wording}
        />
      </Section>
    ) : null;
  } else {
    sections = (
      <>
        {/* The invitation goes to a sponsor who edits or delivers pictures (H4), until somebody
            of the company is in (D94); then only another person is invited. */}
        {writable && invitable && booking.invites && !reached ? (
          <Invitation booking={booking} actionId={action.action_id} locked={locked} run={run} />
        ) : null}
        {writable && reached && sponsorId !== null && can(granted, 'sponsor_access') ? (
          <AccessInvitation
            busy={locked}
            onInvite={(email, done) => {
              run(
                () => sendAccessInvitation(sponsorId, email, action.action_id),
                { onSuccess: done },
              );
            }}
          />
        ) : null}
        <DoorChecklist booking={booking} field={field} wording={wording} />
        <Contribution
          booking={booking}
          actionId={action.action_id}
          mayReview={can(granted, 'door_review')}
          mayAcceptUnscanned={can(granted, 'approve_unscanned')}
          locked={locked}
          run={run}
          writable={writable}
          layoutAllowed={can(granted, 'door_layout')}
          sponsorWorkflow={doorSponsorWorkflow(granted, action)}
          live={action.status === 'live' || action.status === 'paused'}
          customer={customer}
          openAtStart={search.has('bearbeiten')}
        />
      </>
    );
  }

  return (
    <EditorLayout
      preview={
        <ParticipantPreview
          revision={revision}
          defaultMobile
          actionId={action.action_id}
          fieldPosition={field.position}
          title={`Vorschau Türchen ${field.position}`}
        />
      }
    >
      <h2 className="text-xl font-bold">
        Türchen {field.position}
      </h2>

      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 pl-4 text-sm">
        <dt className="text-muted-foreground">Kalendertag</dt>
        <dd>{formatDate(field.calendar_date)}</dd>
        <dt className="text-muted-foreground">Öffnet</dt>
        <dd>{formatOpening(field.opens_at)}</dd>
        <dt className="text-muted-foreground">Tagesfrist</dt>
        <dd>{formatDeadlineAfter(field.opens_at, field.participation_deadline)}</dd>
        {field.sponsor_company !== null ? (
          <>
            <dt className="text-muted-foreground">{wording.nominative}</dt>
            <dd>
              {sponsorId === null ? (
                field.sponsor_company
              ) : (
                <Link className="underline underline-offset-4" to={firmPath(sponsorId, wording)}>
                  {field.sponsor_company}
                </Link>
              )}
            </dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">Zustand</dt>
        <dd>
          <DoorState field={field} wording={wording} />
        </dd>
      </dl>

      {outcome !== null ? <Notice outcome={outcome} /> : null}

      {sections}

      {/* Only once the door has opened: before, every figure is a zero (23.10.2026). */}
      {hasOpened(action, field, systemClock) ? <DoorStatistics field={field} /> : null}

      {can(granted, 'door_design') ? (
        <DoorNumber
          field={field}
          actionId={action.action_id}
          readOnly={!writable}
          onSaved={() => {
            setRevision((value) => value + 1);
            reloadFields();
          }}
        />
      ) : null}

      {/* What takes a booking or the door away, together at the foot of the page. */}
      {writable && booking !== null && can(granted, 'door_placement') ? (
        <ReleaseBooking booking={booking} actionId={action.action_id} locked={locked} run={run} />
      ) : null}
      {writable && can(granted, 'emergency') ? (
        <EmergencyStop field={field} actionId={action.action_id} locked={locked} run={run} />
      ) : null}
    </EditorLayout>
  );
}
